import { PhoneCall } from "lucide-react";
import {
  CALL_ATTEMPT_OUTCOMES,
  CALL_ATTEMPT_OUTCOME_LABELS,
  callSummary,
  type CallAttempt,
} from "@estelle/domain";

import { showToast } from "@/components/shell/toast";
import { Button } from "@/components/ui/button";
import { reportingNow } from "@/lib/reportClock";
import type { useOrderCalls } from "@/modules/orders/calls";

type OrderCalls = ReturnType<typeof useOrderCalls>;

const TIME = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/**
 * Earlier attempts, in the confirm sheet above Call (#636): "Called 2 times ·
 * last today 11:40 by Arun". Nothing when the order has none.
 */
export function CallSummaryLine({ calls }: { calls: OrderCalls }) {
  const summary = callSummary(calls.attempts, reportingNow());
  if (!summary) return null;
  return (
    <p className="bg-muted text-muted-foreground flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs">
      <PhoneCall aria-hidden className="size-3.5 shrink-0" />
      {summary}
    </p>
  );
}

/**
 * "You called at 11:40. How did it go?" (#636): shown on the order when the
 * person comes back from their own call, until they pick an outcome. Picking
 * Confirmed records the call only; the Mark confirmed button above still does
 * the order's confirmation.
 */
export function CallOutcomePrompt({ calls }: { calls: OrderCalls }) {
  const pending: CallAttempt | null = calls.pending;
  if (!pending) return null;
  const busy = calls.outcome.isPending;

  return (
    <section
      aria-label="How did the call go?"
      className="bg-tint-yellow/60 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2"
    >
      <p className="text-sm">
        <span className="font-medium">
          You called at {TIME.format(new Date(pending.startedAt))}.
        </span>{" "}
        How did it go?
      </p>
      <div className="flex flex-wrap gap-2">
        {CALL_ATTEMPT_OUTCOMES.map((outcome) => (
          <Button
            key={outcome}
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            className="min-h-9 max-md:min-h-11"
            onClick={() => {
              calls.outcome.mutate(
                { attemptId: pending.id, outcome },
                {
                  onSuccess: (result) => {
                    if (!result.ok) {
                      showToast({
                        title: "The outcome was not saved",
                        description: result.rejection.message,
                        tone: "error",
                      });
                    }
                  },
                  onError: () => {
                    showToast({
                      title: "The outcome was not saved",
                      description: "Try again in a moment.",
                      tone: "error",
                    });
                  },
                },
              );
            }}
          >
            {CALL_ATTEMPT_OUTCOME_LABELS[outcome]}
          </Button>
        ))}
      </div>
    </section>
  );
}
